import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaUserShield, FaArrowLeft, FaCheck, FaTimes, FaEye, FaEyeSlash, FaLayerGroup, FaSave } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { validateOrganizationToken } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';
import GroupAssignBoard from '../../components/groups/GroupAssignBoard';

const OrgAdmins = () => {
  const navigate = useNavigate();
  const { orgId: secureToken } = useParams();
  const location = useLocation();
  const organizationName = location.state?.organizationName || 'Organization';
  
  const [orgId, setOrgId] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [twofaLoadingAdminId, setTwofaLoadingAdminId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  // Assign Group modal state
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [assignGroupId, setAssignGroupId] = useState('');
  const [assignAssigned, setAssignAssigned] = useState([]);
  const [assignAvailable, setAssignAvailable] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 2,
    status: 1,
    instructor_group_ids: []
  });
  const [formErrors, setFormErrors] = useState({});

  const validateToken = () => {
    const validation = validateOrganizationToken(secureToken);
    if (!validation.valid) {
      toast.error(validation.expired ? 'Navigation token has expired.' : 'Invalid navigation token.');
      navigate('/admin/organizations');
      return false;
    }
    setOrgId(parseInt(validation.orgId));
    setTokenValid(true);
    return true;
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.organizations.getAdmins(orgId, true);
      setAdmins(response.data || []);
    } catch (error) {
      console.error('Error fetching organization admins:', error);
      toast.error('Failed to load admins');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.roles.list();
      setRoles(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.groups.list();
      setGroups(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Parse instructor_group_ids (may come back as string from DB)
  const parseInstructorGroupIds = (value) => {
    let raw = value;
    if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return []; } }
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => parseInt(id)).filter((id) => !Number.isNaN(id));
  };

  const getInstructorGroupNames = (admin) => {
    const ids = parseInstructorGroupIds(admin.instructor_group_ids);
    if (ids.length === 0) return [];
    return ids
      .map((id) => groups.find((g) => parseInt(g.id) === id)?.name)
      .filter(Boolean);
  };

  // Only groups linked to this org
  const orgGroups = groups.filter((g) => g.organization_id && parseInt(g.organization_id) === parseInt(orgId));

  const handleGroupToggle = (groupId) => {
    const id = groupId.toString();
    setFormData((prev) => {
      const exists = prev.instructor_group_ids.includes(id);
      return {
        ...prev,
        instructor_group_ids: exists
          ? prev.instructor_group_ids.filter((value) => value !== id)
          : [...prev.instructor_group_ids, id]
      };
    });
  };

  useEffect(() => { validateToken(); }, [secureToken]);
  useEffect(() => { if (orgId && tokenValid) { fetchAdmins(); fetchGroups(); } }, [orgId, tokenValid]);
  useEffect(() => { fetchRoles(); }, []);

  const getRoleName = (roleValue) => {
    const match = roles.find((role) => parseInt(role.value) === parseInt(roleValue));
    return match ? match.name : 'Unknown';
  };

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (e) => setFilterStatus(e.target.value);

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!editingAdmin && !formData.password.trim()) errors.password = 'Password is required';
    else if (!editingAdmin && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      first_name: admin.first_name || '',
      last_name: admin.last_name || '',
      email: admin.email || '',
      password: '',
      role: admin.role ?? 2,
      status: admin.status,
      instructor_group_ids: parseInstructorGroupIds(admin.instructor_group_ids).map((id) => id.toString())
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleShowAddModal = () => {
    setEditingAdmin(null);
    setFormData({ first_name: '', last_name: '', email: '', password: '', role: 2, status: 1, instructor_group_ids: [] });
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormData({ first_name: '', last_name: '', email: '', password: '', role: 2, status: 1, instructor_group_ids: [] });
    setFormErrors({});
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? parseInt(value) : value
    }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSaveAdmin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setModalLoading(true);
    try {
      const adminData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: parseInt(formData.role) || 2,
        status: parseInt(formData.status),
        instructor_group_ids: formData.instructor_group_ids.map((id) => parseInt(id)).filter((id) => !Number.isNaN(id)),
        ...(formData.password && { password: formData.password })
      };

      if (editingAdmin) {
        await api.organizations.updateAdmin(orgId, editingAdmin.id, adminData);
        toast.success('Admin updated successfully!');
      } else {
        await api.organizations.addAdmin(orgId, adminData);
        toast.success('Admin created successfully!');
      }
      handleCloseModal();
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to save admin');
    } finally {
      setModalLoading(false);
    }
  };

const handleDeleteClick = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;
    setDeleteLoading(adminToDelete.id);
    try {
      await api.organizations.removeAdmin(orgId, adminToDelete.id);
      toast.success('Admin removed successfully!');
      setShowDeleteModal(false);
      setAdminToDelete(null);
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to remove admin');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const handleRestoreAdmin = async (admin) => {
    try {
      await api.organizations.restoreAdmin(orgId, admin.id);
      toast.success('Admin restored successfully!');
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to restore admin');
    }
  };

  const handleToggle2fa = async (targetAdmin) => {
    try {
      if (!targetAdmin?.id) return;

      const adminName = `${targetAdmin.first_name || ''} ${targetAdmin.last_name || ''}`.trim() || targetAdmin.email;
      const nextEnabled = !Boolean(targetAdmin.is_2fa_enabled);
      const confirmMsg = nextEnabled
        ? `Enable 2FA for ${adminName}? They will be forced to set up authenticator on next login.`
        : `Disable 2FA for ${adminName}? This will remove existing 2FA configuration.`;

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(confirmMsg);
      if (!confirmed) return;

      setTwofaLoadingAdminId(targetAdmin.id);
      await api.users.set2faEnabled(targetAdmin.id, nextEnabled);
      toast.success(nextEnabled ? '2FA enabled (setup required on next login)' : '2FA disabled');
      await fetchAdmins();
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      toast.error(error.response?.data?.message || 'Failed to update 2FA setting');
    } finally {
      setTwofaLoadingAdminId(null);
    }
  };

  const handleGoBack = () => navigate('/admin/organizations');

  // ── Assign Group handlers ────────────────────────────────────────────────
  const handleOpenAssignGroupModal = () => {
    setAssignGroupId('');
    setAssignAssigned([]);
    setAssignAvailable([]);
    setShowAssignGroupModal(true);
  };

  const handleCloseAssignGroupModal = () => {
    if (assignSaving) return;
    setShowAssignGroupModal(false);
    setAssignGroupId('');
    setAssignAssigned([]);
    setAssignAvailable([]);
  };

  const handleAssignGroupChange = async (event) => {
    const groupId = event.target.value;
    setAssignGroupId(groupId);
    if (!groupId) { setAssignAssigned([]); setAssignAvailable([]); return; }
    try {
      setAssignLoading(true);
      const response = await api.groups.getInstructors(groupId, orgId);
      if (response.data?.success) {
        setAssignAssigned(response.data.data?.assigned || []);
        setAssignAvailable(response.data.data?.available || []);
      }
    } catch (error) {
      toast.error('Failed to load group instructors.');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSaveAssignments = async () => {
    if (!assignGroupId) return;
    try {
      setAssignSaving(true);
      await api.groups.setInstructors(assignGroupId, assignAssigned.map((u) => u.id), orgId);
      toast.success('Instructor assignments saved successfully.');
      fetchAdmins();
      handleCloseAssignGroupModal();
    } catch (error) {
      toast.error(`Failed to save assignments: ${error.response?.data?.message || error.message}`);
    } finally {
      setAssignSaving(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = (admin.first_name || '').toLowerCase().includes(search) ||
                         (admin.last_name || '').toLowerCase().includes(search) ||
                         (admin.email || '').toLowerCase().includes(search);
    let matchesStatus = true;
    switch (filterStatus) {
      case 'active': matchesStatus = admin.status === 1 && !admin.deleted; break;
      case 'inactive': matchesStatus = admin.status === 0 && !admin.deleted; break;
      case 'deleted': matchesStatus = !!admin.deleted; break;
      default: matchesStatus = !admin.deleted; break;
    }
    return matchesSearch && matchesStatus;
  });

  const getInitials = (admin) => {
    return ((admin.first_name?.[0] || '') + (admin.last_name?.[0] || '')).toUpperCase() || 'A';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            {organizationName}'s Org Admin/Instructor user
          </h5>
        </div>
      </div>

      {/* Search + Filter + Add */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="lg:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={handleFilterChange}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm px-3 py-2"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <button
          onClick={handleShowAddModal}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2  custom-hamburger custom-hamburger-primary p-2"
        >
          <FaPlus className="w-4 h-4" />
          <span className="whitespace-nowrap">Add Admin/Instructor</span>
        </button>
        <button
          onClick={handleOpenAssignGroupModal}
          className="w-full border-1 border-teal-500 bg-white text-teal-600 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 custom-hamburger"
        >
          <FaLayerGroup className="w-4 h-4" />
          <span className="whitespace-nowrap">Assign Group</span>
        </button>
      </div>

      {/* No Data */}
      {filteredAdmins.length === 0 ? (
        <div className="text-center py-10 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            {searchTerm || filterStatus !== 'all'
              ? 'No admin/instructor users found matching your criteria.'
              : 'No admin/instructor users assigned yet. Click "Add Admin/Instructor" to get started.'
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r text-gray-800 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left font-bold">Name</th>
                <th className="py-2 px-3 text-left font-bold">Email</th>
                <th className="py-2 px-3 text-left font-bold">Role</th>
                <th className="py-2 px-3 text-left font-bold">Assigned Group</th>
                <th className="py-2 px-3 text-left font-bold">Status</th>
                <th className="py-2 px-3 text-left font-bold">2FA Enabled</th>
                <th className="py-2 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map((admin) => (
                <tr key={admin.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${admin.deleted ? 'bg-red-50' : ''}`}>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                      {/* <div className="w-10 h-10 rounded-full bg-gradient-to-br text-gray-800 to-pink-600 flex items-center justify-center text-white font-bold shadow-md text-sm">
                        {getInitials(admin)}
                      </div> */}
                      <div>
                        <div className="font-semibold text-gray-800">
                          {admin.first_name} {admin.last_name}
                        </div>
                        <div className="text-xs text-purple-600 flex items-center gap-1">
                          <FaUserShield className="w-3 h-3" /> {getRoleName(admin.role)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-gray-800 break-all">{admin.email}</td>
                  <td className="py-2 px-3 text-left">
                    <Badge bg="warning" className="px-2 py-1 text-xs">{getRoleName(admin.role)}</Badge>
                  </td>
                  <td className="py-2 px-3">
                    {getInstructorGroupNames(admin).length > 0
                      ? getInstructorGroupNames(admin).map((name) => (
                          <Badge key={name} bg="info" className="me-1 mb-1 text-xs">{name}</Badge>
                        ))
                      : <span className="text-gray-400 text-xs">—</span>
                    }
                  </td>
                  <td className="py-2 px-3 text-left">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${admin.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {admin.status === 1 ? <FaCheck /> : <FaTimes />}
                      {admin.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-left">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id={`twofa-admin-${admin.id}`}
                        label={Boolean(admin.is_2fa_enabled) ? 'On' : 'Off'}
                        checked={Boolean(admin.is_2fa_enabled)}
                        disabled={twofaLoadingAdminId === admin.id || Boolean(admin.deleted)}
                        onChange={() => handleToggle2fa(admin)}
                      />
                      {twofaLoadingAdminId === admin.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : null}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2 justify-left">
                      {!admin.deleted ? (
                        <>
                          <button
                            onClick={() => handleShowEditModal(admin)}
                            className="p-1 border-0 border-purple-300 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                            title="Edit Admin"
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(admin)}
                            disabled={deleteLoading === admin.id}
                            className="p-1 border-0 border-red-300 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            title="Remove Admin"
                          >
                            {deleteLoading === admin.id ? (
                              <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <FaTrash className="w-5 h-5" />
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestoreAdmin(admin)}
                          className="p-2 border border-green-300 text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="Restore Admin"
                        >
                          <FaCheck className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingAdmin ? 'Edit Admin' : 'Add New Admin'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <Form onSubmit={handleSaveAdmin}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.first_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter first name"
                  />
                  {formErrors.first_name && <p className="text-red-500 text-xs mt-1">{formErrors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.last_name ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
                    placeholder="Enter last name"
                  />
                  {formErrors.last_name && <p className="text-red-500 text-xs mt-1">{formErrors.last_name}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={editingAdmin}
                  readOnly={editingAdmin}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.email ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm ${editingAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter email address"
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                {editingAdmin && <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>}
              </div>

              <div className="mb-4 relative">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Password {!editingAdmin && <span className="text-red-500">*</span>}
                  {editingAdmin && <span className="text-gray-500 text-xs"> (leave blank to keep current)</span>}
                </label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${formErrors.password ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm pr-10`}
                    placeholder={editingAdmin ? "New password (optional)" : "Enter password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                >
                  {roles.map((role) => (
                    <option
                      key={role.id}
                      value={role.value}
                      disabled={parseInt(role.value) === 0 || parseInt(role.value) === 1}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Organization Groups <span className="text-gray-500 text-xs font-normal">(optional)</span>
                </label>
                <div className="border border-purple-200 rounded-lg p-3 bg-white max-h-48 overflow-y-auto">
                  {orgGroups.length === 0 ? (
                    <p className="text-muted text-sm mb-0">No groups linked to this organization.</p>
                  ) : (
                    orgGroups.map((group) => {
                      const groupId = group.id.toString();
                      return (
                        <Form.Check
                          key={group.id}
                          type="checkbox"
                          id={`org-admin-group-${groupId}`}
                          label={group.name}
                          checked={formData.instructor_group_ids.includes(groupId)}
                          onChange={() => handleGroupToggle(groupId)}
                          className="mb-2"
                        />
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Only organization groups are listed.</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={handleCloseModal} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" type="submit" disabled={modalLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    {modalLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {editingAdmin ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingAdmin ? 'Update Admin' : 'Create Admin'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Assign Group Modal */}
      <Modal show={showAssignGroupModal} onHide={handleCloseAssignGroupModal} size="xl" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white border-0 py-3 px-4 modal-header">
          <Modal.Title className="font-bold text-base text-white flex items-center gap-2">
            <FaLayerGroup /> Assign Group to Instructor
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Select Group</label>
            <select
              value={assignGroupId}
              onChange={handleAssignGroupChange}
              className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-teal-500 outline-none text-sm"
            >
              <option value="">— Select a group —</option>
              {orgGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            {orgGroups.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                No groups linked to this organization. Link groups in Master Setup → Manage Group.
              </p>
            )}
          </div>

          {assignLoading && (
            <div className="flex justify-center py-8">
              <Spinner animation="border" className="text-teal-600" />
            </div>
          )}

          {!assignLoading && assignGroupId && (
            <GroupAssignBoard
              assigned={assignAssigned}
              available={assignAvailable}
              setAssigned={setAssignAssigned}
              setAvailable={setAssignAvailable}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleCloseAssignGroupModal} disabled={assignSaving}
                className="w-full border-1 bg-gray-100 text-gray-700 border-2 border-gray-300 font-semibold rounded-lg text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleSaveAssignments} disabled={assignSaving || !assignGroupId}
                className="w-full border-1 border-teal-500 bg-white text-teal-600 font-semibold rounded-lg text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 disabled:opacity-50">
                {assignSaving
                  ? <><Spinner animation="border" size="sm" /> Saving...</>
                  : <><FaSave /> Save Assignments</>}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Remove Admin</h5>
          <p className="text-gray-600">
            Are you sure you want to remove <strong>{adminToDelete?.first_name} {adminToDelete?.last_name}</strong> from organization admins?
          </p>
          <p className="text-sm text-gray-500">This is a soft delete. The admin can be restored later.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteCancel} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={handleDeleteConfirm} disabled={deleteLoading} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Removing...
                  </>
                ) : (
                  'Remove Admin'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgAdmins;